import type { Role } from '@/types';
import { getTextClassName } from '@nautilusbio/nb-design-system';
import { Accordion } from '@nautilusbio/nb-design-system/Accordion';
import { Button } from '@nautilusbio/nb-design-system/Button';
import { Stack } from '@nautilusbio/nb-design-system/Stack';
import { Text } from '@nautilusbio/nb-design-system/Text';
import clsx from 'clsx';
import { Check } from 'lucide-react';
import React, { type FC, memo, useEffect, useState } from 'react';

import orgSelectorStyles from './styles.module.css';
import type { OrgListItemProps } from './types';
import { formatRoleLabel } from './utils';

/**
 * A single org row in the org switcher list.
 *
 * - Clicking the org name/avatar switches to that org with the currently selected role.
 * - If the org has multiple allowed roles, the row expands to show an inline role selector.
 *   Selecting a role updates the local selection without switching org (the switch fires on the next org row click).
 * - If the org has only one allowed role, no expansion is shown.
 *
 * Uses the Accordion component for smooth, accessible expand/collapse animations.
 */
export const OrgListItem: FC<OrgListItemProps> = memo(({ org, allowedRoles, assumedRole, onSwitch, isPending }) => {
  const [selectedRole, setSelectedRole] = useState<Role | null>(
    assumedRole && allowedRoles.includes(assumedRole) ? assumedRole : (allowedRoles[0] ?? null),
  );

  // Sync selected role when allowedRoles or assumedRole changes
  useEffect(() => {
    if (allowedRoles.length > 0 && (!selectedRole || !allowedRoles.includes(selectedRole))) {
      setSelectedRole(assumedRole && allowedRoles.includes(assumedRole) ? assumedRole : allowedRoles[0]);
    }
  }, [allowedRoles, assumedRole, selectedRole]);

  if (!allowedRoles.length || !selectedRole) return null;

  const hasMultipleRoles = allowedRoles.length > 1;

  // If single role, just show a simple button without expansion
  if (!hasMultipleRoles) {
    return (
      <Button
        data-testid={`org-selector-item-${org.id}`}
        isDisabled={isPending}
        onPress={() => onSwitch(org, selectedRole)}
        size="sm"
        visual="unstyled"
        fullWidth
      >
        <Text as="span">{org.name}</Text>
      </Button>
    );
  }

  // Multiple roles: use Accordion for smooth expand/collapse
  return (
    <Accordion
      data-testid={`org-selector-accordion-${org.id}`}
      label={
        <Stack alignItems="center" fullWidth gap={2} justifyContent="space-around" orientation="horizontal">
          <Stack
            aria-disabled={isPending}
            as="span"
            className={clsx(getTextClassName('sm'), orgSelectorStyles.orgName)}
            data-testid={`org-selector-item-${org.id}`}
            fullWidth
            onClick={isPending ? undefined : () => onSwitch(org, selectedRole)}
            onKeyDown={
              isPending
                ? undefined
                : e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onSwitch(org, selectedRole);
                    }
                  }
            }
            onFocus={e => {
              e.currentTarget.style.outline = '2px solid var(--color-primary)';
            }}
            onBlur={e => {
              e.currentTarget.style.outline = 'none';
            }}
            // oxlint-disable-next-line jsx_a11y/prefer-tag-over-role
            role="button"
            style={isPending ? { cursor: 'not-allowed' } : undefined}
            tabIndex={isPending ? -1 : 0}
          >
            {org.name}
          </Stack>

          <Text as="span" className={orgSelectorStyles.roleText} noWrap size="xs">
            {formatRoleLabel(selectedRole)}
          </Text>
        </Stack>
      }
      gap={1}
      orientation="vertical"
      showChevron
      chevronPosition="end"
    >
      <Stack className={orgSelectorStyles.roleSelectorContainer} fullWidth gap={1} orientation="horizontal">
        {allowedRoles.map(role => {
          const isSelected = role === selectedRole;

          return (
            <Button
              className={orgSelectorStyles.currentRoleOrgSelector}
              data-testid={`org-role-${org.id}-${role}`}
              key={role}
              onPress={() => setSelectedRole(role)}
              size="xs"
              visual={isSelected ? 'success' : 'outline'}
            >
              {formatRoleLabel(role)}

              {isSelected && <Check size={10} />}
            </Button>
          );
        })}
      </Stack>
    </Accordion>
  );
});

OrgListItem.displayName = 'OrgListItem';
